import { useEffect, useState } from 'react'
import { httpsCallable, getFunctions } from 'firebase/functions'

/**
 * useTicketClaims
 *
 * After sign-in, mints the caller's ticket custom claims and reads them back
 * from a force-refreshed ID token so Firestore/Storage security rules (the real
 * authority) and the client agree on the user's role.
 *
 * Flow (keyed on `user`):
 *  - No user: reset to signed-out state.
 *  - Call the idempotent `setTicketClaims` callable, then force-refresh the ID
 *    token via `getIdTokenResult(true)` and read `admin` / `customerId`.
 *  - If neither claim is present (claims not yet propagated), retry once.
 *  - Resolution completing sets `ready: true` even when the user has no role.
 *
 * @param {Object} [user] Firebase auth user, or undefined when signed out.
 * @returns {{ admin: boolean, customerId: (string|null), ready: boolean, loading: boolean }}
 */
const useTicketClaims = (user) => {
  const [admin, setAdmin] = useState(false)
  const [customerId, setCustomerId] = useState(null)
  const [ready, setReady] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    if (!user) {
      setAdmin(false)
      setCustomerId(null)
      setReady(false)
      setLoading(false)
      return
    }

    const setTicketClaims = httpsCallable(getFunctions(), 'setTicketClaims')

    const refreshClaims = async () => {
      await setTicketClaims()
      const { claims } = await user.getIdTokenResult(true)
      return {
        admin: claims.admin === true,
        customerId: claims.customerId || null
      }
    }

    const resolveClaims = async () => {
      setLoading(true)
      try {
        let resolved = await refreshClaims()

        // Stale claims: neither role present after the first refresh. Retry once.
        if (!resolved.admin && !resolved.customerId) {
          resolved = await refreshClaims()
        }

        if (cancelled) return

        setAdmin(resolved.admin)
        setCustomerId(resolved.customerId)
        setReady(true)
        setLoading(false)
      } catch (err) {
        console.error('Failed to resolve ticket claims', err)
        if (cancelled) return
        setReady(false)
        setLoading(false)
      }
    }

    resolveClaims()

    return () => {
      cancelled = true
    }
  }, [user])

  return { admin, customerId, ready, loading }
}

export default useTicketClaims
